import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  StudentPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./StudentPage.styles";
import Sidebar from "../../../components/Sidebar";
import Table from "../../../components/Table";
import IconButton from "../../../components/Buttons/IconButton";
import { Toast } from "primereact/toast";
import delIcon from "../../../assets/icons/delIcon.svg";
import editIcon from "../../../assets/icons/editIcon.svg";
import { Student } from "../../../services/documentService/documentService.types";
import { fetchStudents, createStudent, updateStudent, deleteStudent } from "../../../services/documentService/documentService";
import Button from "../../../components/Buttons/Button";
import { ButtonWrapper } from "../TemplatesPage.styles";
import { ConfirmModal, CreateModal } from "../../../components/Modals";
import DeletedRow from "../../../components/DeletedRow";
import RestoredRow from "../../../components/RestoredRow";
import BreadcrumbLink from "../../../components/BreadcrumbLink/BreadcrumbLink";
import { ButtonsContainer } from "../../DocumentVersionsPage/DocumentVersionsPage.styles";
import { formatServerErrors } from "../../../utils/errorHandler";

const StudentPage: React.FC = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [orderBy, setOrderBy] = useState<string>("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [studentToEdit, setStudentToEdit] = useState<Student | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useRef<Toast>(null);

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);

  // Состояния для отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [searchParams] = useSearchParams();
  const documentName = searchParams.get("documentName") || "Неизвестный документ";
  const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
  const { documentId } = useParams<{ documentId: string }>();
  const templateId = searchParams.get("templateId") || "0";

  useEffect(() => {
    if (!templateId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать документ.",
      });
      navigate("/login");
      return;
    }
    loadStudents();
  }, [templateId, currentPage, pageSize, orderBy]);

  const loadStudents = async () => {
    if (!templateId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось получить ID документа",
        life: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetchStudents(Number(documentId), currentPage, pageSize, orderBy);
      setStudents(response.results);
      setTotalPages(Math.ceil(response.count / pageSize));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Ошибка загрузки студентов",
        life: 3000,
      });
      console.error("Ошибка при загрузке студентов:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy("");
    } else {
      setOrderBy(field);
    }
  };

  const openDeleteModal = (student: Student) => {
    setStudentToDelete(student);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setStudentToDelete(null);
  };

  const confirmDelete = () => {
    if (studentToDelete) {
      handleDelete(studentToDelete.id);
      closeDeleteModal();
    }
  };

  // Отложенное удаление с задержкой в 5 секунд
  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteStudent(id);
        setPendingDelete(null);
        toast.current?.show({
          severity: "success",
          summary: "Удалено",
          detail: "Студент успешно удален",
          life: 2000,
        });
        loadStudents();
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить студента",
          life: 3000,
        });
        console.error("Ошибка при удалении студента:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  const restoreStudentHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.current?.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление студента отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const openCreateModal = () => {
    setStudentToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (student: Student) => {
    setStudentToEdit(student);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrEditStudent = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      if (studentToEdit) {
        await updateStudent(studentToEdit.id, { 
          FIO: formData["FIO"] as string, 
          passport_data: formData["passport_data"] as string, 
          SNILS: formData["SNILS"] as string, 
          version: documentId 
        });
        setRecentlyEdited(studentToEdit.id);
        toast.current?.show({
          severity: "success",
          summary: "Успех",
          detail: "Студент обновлен",
          life: 3000,
        });
      } else {
        const createdStudent = await createStudent(Number(documentId), { 
          FIO: formData["FIO"] as string, 
          passport_data: formData["passport_data"] as string, 
          SNILS: formData["SNILS"] as string, 
          version: documentId 
        });
        setRecentlyCreated(createdStudent.id);
        toast.current?.show({
          severity: "success",
          summary: "Успех",
          detail: "Студент создан",
          life: 3000,
        });
      }
      closeCreateModal();
      loadStudents();
      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
      } else {
        console.error("Ошибка при сохранении студента:", error);
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения студента",
          life: 3000,
        });
      }
    }
  };

  const headers = [
    { label: "ФИО", key: "FIO", fieldName: "FIO" },
    { label: "Паспортные данные", key: "passport_data", fieldName: "passport_data", sortable: false },
    { label: "СНИЛС", key: "SNILS", fieldName: "SNILS", sortable: false },
    { label: "", key: "buttons", sortable: false },
  ];

  const rows = students.map((student) => {
    if (student.id === pendingDelete) {
      return {
        FIO: (
          <DeletedRow
            id={student.id}
            onRestore={restoreStudentHandler}
            colSpan={headers.length}
            message={`Студент "${student.FIO}" будет удален через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      FIO: student.FIO,
      passport_data: (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "300px",
          }}
          title={student.passport_data}
        >
          {student.passport_data}
        </div>
      ),
      SNILS: (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          }}
          title={student.SNILS}
        >
          {student.SNILS}
        </div>
      ),
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(student)}
          />
          <IconButton
            icon={delIcon}
            altText="Удалить"
            onClick={() => openDeleteModal(student)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставка уведомлений
  if (recentlyRestored !== null) {
    const restoredIndex = students.findIndex((s) => s.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        FIO: (
          <RestoredRow colSpan={headers.length} message="Студент успешно восстановлен" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = students.findIndex((s) => s.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        FIO: (
          <RestoredRow colSpan={headers.length} message="Студент успешно создан" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = students.findIndex((s) => s.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        FIO: (
          <RestoredRow colSpan={headers.length} message="Студент успешно отредактирован" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "10 записей", value: 10 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <StudentPageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/students?documentName=${documentName}&templateId=${templateId}}&templateName=${templateName}`}
            text={`Студенты документа ${documentName}`}
          />
        </div>
        <HeaderContainer>
          <Title>Студенты</Title>
          <ButtonWrapper>
            <Button onClick={openCreateModal} variant="active">
              Добавить студента
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="FIO"
        />
      </ContentWrapper>
      {isCreateModalOpen && (
        <CreateModal
          title={studentToEdit ? "Редактировать студента" : "Новый студент"}
          fields={[
            { name: "FIO", type: "text", placeholder: "ФИО", required: true },
            { name: "passport_data", type: "text", placeholder: "Паспортные данные", required: true },
            { name: "SNILS", type: "text", placeholder: "СНИЛС", required: true },
          ]}
          onSubmit={handleCreateOrEditStudent}
          submitButtonText={studentToEdit ? "Сохранить" : "Создать"}
          onCancel={closeCreateModal}
          initialValues={studentToEdit ? { FIO: studentToEdit.FIO, passport_data: studentToEdit.passport_data, SNILS: studentToEdit.SNILS } : {}}
          externalErrors={externalErrors}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить студента "${studentToDelete?.FIO}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </StudentPageContainer>
  );
};

export default StudentPage;
